import { memo, useCallback, useRef, useState, useEffect } from 'react';
import { useOnValueChange } from '../../../hooks/useOnValueChange';
import { AnnotationContextGroup } from '../../../contexts/AnnotationContext';
import { PropertiesSection, Property } from './PropertiesSection';
import { useSurfaceProperties } from '../../../modules/Properties/integration/annotations/SurfaceProperties';
import { useT } from '../../../translation/src';
import { useCondMeterToFeet, useCondFeetToMeter } from '../../../hooks/useFormatNumber';
import { InputWithUnit } from '../../../components/inputs/Input/InputWithUnit';
import { ConvertType } from '../../../hooks/useFormatNumber';
import { SurfaceKind } from 'shared-types';
import { Option, Select } from '../../../components/inputs/Select';
import { useProjectUpdateEntityNameMutation } from '../../../types/graphqlTypes';
import { PropertyPanel } from '../../Properties/components/PropertyPanel';
import { AnnotationType } from '../../Properties/types';
import { ChildPropertiesSection } from './ChildPropertiesSection';

interface SurfacePropertiesProps {
  projectId: string;
  group: AnnotationContextGroup;
}

// Convert radians to degrees for display
const radToDeg = (rad: number) => (rad * 180) / Math.PI;
// Convert degrees to radians for storage
const degToRad = (deg: number) => (deg * Math.PI) / 180;

// Default names for surfaces - used to check if name should auto-update
const DEFAULT_SURFACE_NAMES = ['Surface', 'Section'];

const SurfaceProperties_: React.FC2<SurfacePropertiesProps> = ({ projectId, group }) => {
  // Surface plane properties
  const { getSurfacePlanePositionProperty, getSurfacePlaneNormalProperty, getSurfaceToleranceProperty, getSurfacePlaneRotationProperty, getSurfaceKindProperty } = useSurfaceProperties(projectId);

  const [updateEntityName] = useProjectUpdateEntityNameMutation();

  const { condMeterToFeet } = useCondMeterToFeet();
  const { condFeetToMeter } = useCondFeetToMeter();

  // Translations
  const useTranslations = () => {
    const information = useT('information', { swc: true });
    const surface = useT('surface', { swc: true });
    const section = useT('section', { swc: true });
    const surfaceType = useT('surface type', { swc: true });
    const position = useT('position', { swc: true });
    const normal = useT('normal', { swc: true });
    const tolerance = useT('tolerance', { swc: true });
    const rotation = useT('rotation', { swc: true });

    return {
      information,
      surface,
      section,
      surfaceType,
      position,
      normal,
      tolerance,
      rotation,
    };
  };

  const translations = useTranslations();

  // Get reactive properties
  const surfacePositionProperty = getSurfacePlanePositionProperty(group.identifier);
  const surfaceNormalProperty = getSurfacePlaneNormalProperty(group.identifier);
  const surfaceToleranceProperty = getSurfaceToleranceProperty(group.identifier);
  const surfaceRotationProperty = getSurfacePlaneRotationProperty(group.identifier);
  const surfaceKindProperty = getSurfaceKindProperty(group.identifier);

  // Subscribe to property values using useSyncExternalStore for proper React integration
  const surfacePosition = surfacePositionProperty.value;
  const surfaceNormal = surfaceNormalProperty.value;

  // Surface kind state
  const [surfaceKindValue, setSurfaceKindValue] = useState<SurfaceKind>(() => (surfaceKindProperty.value as SurfaceKind) || SurfaceKind.SURFACE);

  // Sync surface kind with property changes
  useEffect(() => {
    const sub = surfaceKindProperty.subscribeToChanges((newValue) => {
      setSurfaceKindValue((newValue as SurfaceKind) || SurfaceKind.SURFACE);
    });
    return () => sub.unsubscribe();
  }, [surfaceKindProperty]);

  // Track if user is editing to prevent external updates from overwriting input
  const isEditingToleranceRef = useRef(false);
  const isEditingRotationRef = useRef(false);

  // Use string state for input to allow partial typing (e.g., "1." or empty string)
  // Only format as fixed decimal when not editing
  const getToleranceDisplayValue = useCallback((meters: number) => condMeterToFeet(meters || 0.1).toFixed(2), [condMeterToFeet]);

  const getRotationDisplayValue = useCallback((radians: number) => radToDeg(radians || 0).toFixed(1), []);

  const [toleranceInputValue, setToleranceInputValue] = useState<string>(() => getToleranceDisplayValue(surfaceToleranceProperty.value as number));

  const [rotationInputValue, setRotationInputValue] = useState<string>(() => getRotationDisplayValue(surfaceRotationProperty.value as number));

  // Per-surface drafts are reset by the parent keying this component on `group.identifier`
  // (see RendererRightPanel). The tolerance draft is shown in the user's unit, so it is
  // re-formatted when the unit preference changes: `getToleranceDisplayValue` tracks
  // `condMeterToFeet`, which tracks the preference.
  useOnValueChange(getToleranceDisplayValue, (format) => setToleranceInputValue(format(surfaceToleranceProperty.value as number)));

  // Sync tolerance input value with property changes, but only when not editing
  useEffect(() => {
    const sub = surfaceToleranceProperty.subscribeToChanges((newValue) => {
      if (!isEditingToleranceRef.current) {
        setToleranceInputValue(getToleranceDisplayValue(newValue as number));
      }
    });
    return () => sub.unsubscribe();
  }, [surfaceToleranceProperty, getToleranceDisplayValue]);

  // Sync rotation input value with property changes, but only when not editing
  useEffect(() => {
    const sub = surfaceRotationProperty.subscribeToChanges((newValue) => {
      if (!isEditingRotationRef.current) {
        setRotationInputValue(getRotationDisplayValue(newValue as number));
      }
    });
    return () => sub.unsubscribe();
  }, [surfaceRotationProperty, getRotationDisplayValue]);

  // Format position for display
  const formattedPosition = surfacePosition ? `(${condMeterToFeet(surfacePosition.x).toFixed(2)}, ${condMeterToFeet(surfacePosition.y).toFixed(2)}, ${condMeterToFeet(surfacePosition.z).toFixed(2)})` : '-';

  // Format normal for display (normals are unit vectors, no conversion needed)
  const formattedNormal = surfaceNormal ? `(${surfaceNormal.x.toFixed(3)}, ${surfaceNormal.y.toFixed(3)}, ${surfaceNormal.z.toFixed(3)})` : '-';

  // Handle tolerance change - store raw string input, only update property when valid
  const onEditTolerance = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value;
    // Always update the input string to allow typing
    setToleranceInputValue(rawValue);

    // Only update the property if the value is a valid positive number
    const displayValue = parseFloat(rawValue);
    if (!isNaN(displayValue) && displayValue > 0) {
      // Convert from display units (possibly feet) back to meters
      const newTolerance = condFeetToMeter(displayValue);
      // Update the property (will trigger save)
      // eslint-disable-next-line react-hooks/immutability -- ReactiveProperty is an external store, not React state
      surfaceToleranceProperty.value = newTolerance;
    }
  };

  // Track focus state to know when user is editing tolerance
  const onToleranceFocus = useCallback(() => {
    isEditingToleranceRef.current = true;
  }, []);

  // On blur, format the value properly and sync with property
  const onToleranceBlur = () => {
    isEditingToleranceRef.current = false;
    // Format the display value properly when done editing
    setToleranceInputValue(getToleranceDisplayValue(surfaceToleranceProperty.value as number));
  };

  // Handle rotation change - store raw string input, only update property when valid
  const onEditRotation = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value;
    // Always update the input string to allow typing
    setRotationInputValue(rawValue);

    // Only update the property if the value is a valid number
    const displayValue = parseFloat(rawValue);
    if (!isNaN(displayValue)) {
      // Convert from degrees to radians
      const newRotation = degToRad(displayValue);
      // Update the property (will trigger save)
      // eslint-disable-next-line react-hooks/immutability -- ReactiveProperty is an external store, not React state
      surfaceRotationProperty.value = newRotation;
    }
  };

  // Track focus state to know when user is editing rotation
  const onRotationFocus = useCallback(() => {
    isEditingRotationRef.current = true;
  }, []);

  // On blur, format the value properly and sync with property
  const onRotationBlur = () => {
    isEditingRotationRef.current = false;
    // Format the display value properly when done editing
    setRotationInputValue(getRotationDisplayValue(surfaceRotationProperty.value as number));
  };

  // Handle surface kind change
  // Not wrapped in useCallback: `Select` is memo'd but always receives fresh `children`
  // (the <Option> elements), so its memo never hits here and a stable handler buys nothing.
  const onSurfaceKindChange = (event: { target: { value: SurfaceKind; name: string } }) => {
      const newKind = event.target.value;
      setSurfaceKindValue(newKind);
      // eslint-disable-next-line react-hooks/immutability -- ReactiveProperty is an external store, not React state
      surfaceKindProperty.value = newKind;

      // Update the name if it's still a default name
      const currentName = group.name;
      if (DEFAULT_SURFACE_NAMES.includes(currentName)) {
        const newName = newKind === SurfaceKind.SECTION ? 'Section' : 'Surface';
        if (newName !== currentName) {
          updateEntityName({
            variables: {
              projectId,
              name: newName,
              groupIdentifier: group.identifier,
            },
          });
        }
      }
  };

  return (
    <div className="flex flex-col space-y-4 divide-y divide-gray-800">
      <PropertiesSection title={translations.information}>
        <Property title={translations.position}>{formattedPosition}</Property>
        <Property title={translations.normal}>{formattedNormal}</Property>
        <Property title={translations.tolerance}>
          <InputWithUnit
            key={group.identifier.concat('Tolerance')}
            id="inputSurfaceTolerance"
            onChange={onEditTolerance}
            onFocus={onToleranceFocus}
            onBlur={onToleranceBlur}
            min={0.01}
            step="0.01"
            type="number"
            value={toleranceInputValue}
            unit={ConvertType.DISTANCE}
          />
        </Property>
        <Property title={translations.surfaceType}>
          <Select<SurfaceKind> key={group.identifier.concat('SurfaceKind')} name="surfaceKind" value={surfaceKindValue} onChange={onSurfaceKindChange} className="w-full">
            <Option value={SurfaceKind.SURFACE}>{translations.surface}</Option>
            <Option value={SurfaceKind.SECTION}>{translations.section}</Option>
          </Select>
        </Property>
        <Property title={translations.rotation}>
          <InputWithUnit
            key={group.identifier.concat('Rotation')}
            id="inputSurfaceRotation"
            onChange={onEditRotation}
            onFocus={onRotationFocus}
            onBlur={onRotationBlur}
            min={-360}
            max={360}
            step="1"
            type="number"
            value={rotationInputValue}
            unitString="°"
          />
        </Property>
      </PropertiesSection>

      <PropertyPanel projectId={projectId} annotationTypes={[AnnotationType.SURFACE]} objectIdentifiers={[group.identifier]} />
      <ChildPropertiesSection key={group.identifier} projectId={projectId} annotations={group.annotations} />
    </div>
  );
};

export const SurfaceProperties = memo(SurfaceProperties_);
